import {
  assignTaskMutationKey,
  myTasksQueryKey,
  type Task,
  taskByIdQueryKey,
  taskEventsQueryKey,
  tasksQueryKey,
  useClaimTaskMutation,
  useReleaseTaskMutation,
} from '@cartwheel/api/v1'
import { Button, useToast } from '@cartwheel/components'
import { useIsMutating, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

export function TaskClaimControl({ task, currentUserId }: { task: Task; currentUserId: string }) {
  const [confirmingTakeover, setConfirmingTakeover] = useState(false)
  const queryClient = useQueryClient()
  const toast = useToast()
  const assignmentMutationPending = useIsMutating({ mutationKey: assignTaskMutationKey() }) > 0
  const detailQueryKey = taskByIdQueryKey({ path: { id: task.id } })

  const refreshRelatedTaskData = () => {
    void queryClient.invalidateQueries({ queryKey: tasksQueryKey() })
    void queryClient.invalidateQueries({ queryKey: myTasksQueryKey() })
    void queryClient.invalidateQueries({
      queryKey: taskEventsQueryKey({ path: { task_id: task.id } }),
    })
  }

  const refreshTaskData = () => {
    refreshRelatedTaskData()
    void queryClient.invalidateQueries({ queryKey: detailQueryKey })
  }

  const claimMutation = useClaimTaskMutation({
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: detailQueryKey })
    },
    onSuccess: async (data) => {
      await queryClient.cancelQueries({ queryKey: detailQueryKey })
      queryClient.setQueryData(detailQueryKey, data)
      setConfirmingTakeover(false)
      refreshRelatedTaskData()
    },
    onError: () => {
      refreshTaskData()
      toast.error({ description: 'Unable to start task' })
    },
  })

  const releaseMutation = useReleaseTaskMutation({
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: detailQueryKey })
    },
    onSuccess: async (data) => {
      await queryClient.cancelQueries({ queryKey: detailQueryKey })
      queryClient.setQueryData(detailQueryKey, data)
      refreshRelatedTaskData()
    },
    onError: () => {
      refreshTaskData()
      toast.error({ description: 'Unable to release task' })
    },
  })

  if (task.status !== 'open') return null

  const isClaimed = Boolean(task.claimed_at)
  const isClaimedBySomeoneElse = isClaimed && task.assignee_id !== currentUserId
  const isPending =
    claimMutation.isPending || releaseMutation.isPending || assignmentMutationPending

  const startTask = () => {
    if (queryClient.isMutating({ mutationKey: assignTaskMutationKey() })) return
    claimMutation.mutate({ path: { id: task.id } })
  }
  const releaseTask = () => {
    if (queryClient.isMutating({ mutationKey: assignTaskMutationKey() })) return
    releaseMutation.mutate({ path: { id: task.id } })
  }

  if (task.capabilities.can_release) {
    return (
      <Button className="w-full" variant="secondary" disabled={isPending} onClick={releaseTask}>
        {releaseMutation.isPending ? 'Releasing…' : 'Release task'}
      </Button>
    )
  }

  if (!task.capabilities.can_claim) return null

  if (confirmingTakeover && task.assignee && isClaimedBySomeoneElse) {
    return (
      <div>
        <h3 className="text-base font-semibold leading-[1.5] text-charcoal">Take over task?</h3>
        <p className="mt-1 text-sm leading-[1.5] text-charcoal-50">
          This will reassign the task to you.
        </p>
        <div className="mt-4 flex gap-2">
          <Button
            className="flex-1"
            variant="secondary"
            disabled={isPending}
            onClick={() => setConfirmingTakeover(false)}
          >
            Cancel
          </Button>
          <Button className="flex-1" disabled={isPending} onClick={startTask}>
            {claimMutation.isPending ? 'Starting…' : 'Take over'}
          </Button>
        </div>
      </div>
    )
  }

  if (isClaimedBySomeoneElse && task.assignee) {
    return (
      <Button
        className="w-full"
        variant="secondary"
        disabled={isPending}
        onClick={() => setConfirmingTakeover(true)}
      >
        Take over task
      </Button>
    )
  }

  return (
    <Button className="w-full" disabled={isPending} onClick={startTask}>
      {claimMutation.isPending ? 'Starting…' : 'Start task'}
    </Button>
  )
}
